import React, { useEffect, useRef } from 'react';
import './aurora-auth.css';

export interface AuroraAuthLayoutProps {
  videoSrc: string;
  videoOverlay?: 'none' | 'subtle';
  videoPlaybackRate?: number;
  /** Omit for fullscreen login (no left carousel / hero column). */
  hero?: React.ReactNode;
  children: React.ReactNode;
}

export const AuroraAuthLayout: React.FC<AuroraAuthLayoutProps> = ({
  videoSrc,
  videoOverlay = 'subtle',
  videoPlaybackRate = 0.65,
  hero,
  children,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fullscreen = hero == null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const apply = () => {
      video.playbackRate = videoPlaybackRate;
    };
    apply();
    video.addEventListener('loadedmetadata', apply);
    return () => video.removeEventListener('loadedmetadata', apply);
  }, [videoPlaybackRate]);

  const videoLayer = (
    <>
      <video
        ref={videoRef}
        className="aurora-auth__video"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      {videoOverlay === 'subtle' && <div className="aurora-auth__video-scrim--subtle" aria-hidden />}
    </>
  );

  if (fullscreen) {
    return (
      <main className="aurora-auth aurora-auth--fullscreen">
        <div className="aurora-auth__backdrop" aria-hidden>
          {videoLayer}
        </div>
        <section className="aurora-auth__panel" aria-label="Anmeldung">
          {children}
        </section>
      </main>
    );
  }

  return (
    <main className="aurora-auth">
      <aside className="aurora-auth__hero" aria-label="ARKORBIT Produkt-Highlights">
        {videoLayer}
        <div className="aurora-auth__hero-inner">{hero}</div>
      </aside>
      <section className="aurora-auth__panel" aria-label="Anmeldung">
        {children}
      </section>
    </main>
  );
};
